import { defineRecipe } from '@chakra-ui/react';

/**
 * Плита — поверхность, на которой лежит содержимое страницы.
 *
 * Заводится рецептом по той же причине, что и чип: общий компонент можно не позвать. Рецепт чипа
 * живёт в теме давно, а восемьдесят с лишним файлов всё равно слепили пилюлю руками рядом с ним —
 * потому что «взять из темы» требует вспомнить, что она там есть. Рецепт же достаётся вместе с
 * темой и применяется одним словом на любом узле.
 *
 * До этого плита собиралась шестью разными сочетаниями материала, края и скругления:
 * карточка с границей на 12 и она же на 8, поверхность с тенью на 12 и она же на 8, карточка с
 * тенью, заливка без края. Выбор делался на каждом экране заново — и делался иначе, поэтому в
 * «Поиске» левая колонка держится границей, а правая тенью, в одном поле зрения.
 *
 * Три роли закрывают всё, что реально встречается. Роль отвечает на вопрос «чем эта коробка
 * является», а не «как она выглядит»:
 *
 * - `object` — то, что человек воспринимает как отдельный предмет и может открыть: карточка
 *   заявки, карточка сессии, плитка игры, день расписания. Держится границей: предмет имеет край.
 * - `container` — колонка или список, который держит разнородное содержимое: обе колонки страницы
 *   заявки, плита профиля, списки друзей, календарь сессий. Держится тенью: контейнер не предмет,
 *   у него не край, а глубина.
 * - `nested` — блок внутри объекта или контейнера: комментарии в карточке ленты, панели внутри
 *   игровых стат-плашек. Ни края, ни тени — только заливка, иначе вложенность читается как ещё
 *   одна коробка того же уровня.
 *
 * Скругление у контейнера намеренно оставлено прежним (`lg`, 8px), хотя по шкале ему полагается
 * крупная ступень: этот рецепт заводится как замена один-в-один, чтобы интерфейс не менялся ни на
 * пиксель. Поднять его до 16px — отдельное видимое решение, и когда оно будет принято, править
 * придётся одну строку здесь, а не одиннадцать мест по экранам. В этом и смысл рецепта.
 *
 * Четвёртой роли нет намеренно. Коробка, которая не подошла ни к одной из трёх, — это либо
 * объект, либо контейнер, либо вложенное; третьего смысла у поверхности не бывает.
 */
export const panelRecipe = defineRecipe({
  className: 'tp-panel',
  base: {
    minWidth: 0,
  },
  variants: {
    variant: {
      object: {
        bgColor: 'bg.card',
        borderWidth: '1px',
        borderColor: 'border.subtle',
        borderRadius: 'xl',
      },
      container: {
        bgColor: 'bg.surface',
        boxShadow: 'md',
        // Крупная ступень: контейнер держит внутри объекты со скруглением 12, и на 8 вложенное
        // оказывалось круглее коробки, в которой лежит, — коробка визуально «выпирала».
        borderRadius: '2xl',
      },
      nested: {
        bgColor: 'bg.muted',
        borderRadius: 'lg',
      },
    },
  },
  defaultVariants: {
    variant: 'object',
  },
});
